import React, { useMemo, useState } from 'react';

import useTheme from '../../../../hooks/use-theme/useTheme';

import { getClassNames } from '../../../../utils/util';

import AuthLayout from '../../../../layouts/auth/AuthLayout';
import Button from '../../../common/button/Button';
import PasswordInput from '../../../common/input/password-input/PasswordInput';
import PasswordValidation from '../../../common/validation/password-validation/PasswordValidation';
import UsernameValidation from '../../../common/validation/username-validation/UsernameValidation';

import { ReactComponent as UserIcon } from '../../../../resources/icons/user.svg';

import './activate.scss';

type Props = {
  createSystemUser: (username: string, password: string) => void;
  programmeName?: string;
  memberStatus?: string;
};

const Activate = ({ createSystemUser, programmeName, memberStatus }: Props) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationPassword, setConfirmationPassword] = useState('');
  const [validUsername, setValidUsername] = useState(false);
  const [validPassword, setValidPassword] = useState(false);

  const { pps } = useTheme();

  const isMemberActivated = useMemo(() => {
    if (!memberStatus) return false;

    return memberStatus.toLowerCase() === 'active';
  }, [memberStatus]);

  return (
    <AuthLayout>
      {!programmeName && !isMemberActivated && (
        <div className="activate">
          <div className="activate__messages">
            <p className={getClassNames('activate__messages__title', { pps })}>Welcome!</p>
            <p className={getClassNames('activate__messages__subtitle', { pps })}>
              Please choose a username and password
            </p>
          </div>

          <div className="activate__inputs">
            <div className={getClassNames('activate__input-container', { pps })}>
              <UserIcon className={getClassNames('activate__user-icon', { pps })} />
              <input
                className={getClassNames('activate__input', { pps })}
                type="text"
                value={username}
                onChange={({ target }) => setUsername(target.value)}
                placeholder="username"
              />
            </div>

            <UsernameValidation username={username} setValidUsername={setValidUsername} />

            <PasswordInput
              value={password}
              onChange={({ target }) => setPassword(target.value)}
              placeholder="password"
            />

            <PasswordInput
              value={confirmationPassword}
              onChange={({ target }) => setConfirmationPassword(target.value)}
              placeholder="confirm password"
            />

            <PasswordValidation
              password={password}
              confirmationPassword={confirmationPassword}
              setValidPassword={setValidPassword}
            />
          </div>

          <div className="activate__button-container">
            <Button
              text="Register"
              onClick={() => createSystemUser(username, password)}
              disabled={!validUsername || !validPassword}
              auth={!pps}
            />
          </div>
        </div>
      )}

      {programmeName && (
        <div className="activate">
          <div className={getClassNames('activate__success-message', { pps })}>
            <p>Success!</p>
            <div>
              <p>You have been successfully</p>
              <p>registered for the programme</p>
              <p>
                <b>{programmeName}</b>
              </p>
            </div>
            <Instructions />
          </div>
        </div>
      )}

      {isMemberActivated && (
        <div className="activate">
          <div className={getClassNames('activate__success-message', { pps })}>
            <p>Success!</p>
            <p>You have already registered!</p>
            <Instructions />
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

const Instructions = () => (
  <div>
    <p>Please download and open the app</p>
    <p>on your preferred mobile device</p>
    <p>and log in</p>
    <p>(see FAQ or email for further instructions)</p>
  </div>
);

export default Activate;
